import { Aside, getImgProps } from '@superhi/design'
import React from 'react'

import Base from '../Base'
import Share from '../Base/components/Share'
import { useFullPageUrl } from '../../../../components/utils'

import ArticleBody from './components/ArticleBody'
import AuthorBio from './components/AuthorBio'
import { HeroImage } from './styles'

type Props = Omit<React.ComponentProps<typeof Base>, 'header' | 'imageUrl' | 'type'> & {
  body: string
  heroImage: { url: string; blurhash: string; width: number; height: number }
  image: { url: string; width: number; height: number }
  illustratorName?: string
  illustratorUrl?: string
  authorName: string
  authorUrl?: string
  authorBio?: string
}

const PostArticle: React.FC<Props> = ({
  title,
  body,
  authorName,
  authorUrl,
  authorBio,
  image,
  heroImage,
  illustratorName,
  illustratorUrl,
  ...props
}) => {
  const metaItems = React.useMemo<React.ComponentProps<typeof Base>['metaItems']>(() => {
    const i = [
      {
        title: 'Author',
        content: authorName,
        href: authorUrl,
        isExternal: true,
      },
    ]

    return illustratorName
      ? [
          ...i,
          {
            title: 'Illustrator',
            href: illustratorUrl,
            content: illustratorName,
            isExternal: true,
          },
        ]
      : i
  }, [authorName, authorUrl, illustratorName, illustratorUrl])

  const heroImageProps = React.useMemo(
    () =>
      getImgProps(heroImage.url, {
        small: 100,
        medium: 100,
        large: 100,
        xlarge: 100,
      }),
    [heroImage.url],
  )

  const fullPageUrl = useFullPageUrl()

  return (
    <Base
      {...props}
      type="article"
      title={title}
      metaItems={metaItems}
      imageUrl={image.url}
      header={
        <HeroImage
          {...heroImageProps}
          blurHash={heroImage.blurhash}
          width={heroImage.width}
          height={heroImage.height}
        />
      }
    >
      <ArticleBody>{body}</ArticleBody>

      <Aside label="Share this post" level="2">
        <Share title={title} url={fullPageUrl} />
      </Aside>

      {authorBio && authorBio !== '{}' && (
        <Aside label="About the author" level="2">
          {authorBio && <AuthorBio>{authorBio}</AuthorBio>}
        </Aside>
      )}
    </Base>
  )
}

export default PostArticle
